import { Button, Flex, Text } from '@billy/design-system'
import { type ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useComponentId, useFormContext } from '../../../../../../../../../../../../hooks'
import { LegalMentionsType } from '../../../../../../../../enums/legalMentionsType'
import type { ComplianceSettingsFormSchema } from '../../../../../../../../types/complianceSettingsFormSchema'
import * as Styled from './styles'

interface LegalMentionRowProps {
  legalMentionsType: LegalMentionsType
  onEdit: (legalMentionsType: LegalMentionsType) => void
}

export const LegalMentionRow = ({ legalMentionsType, onEdit }: LegalMentionRowProps): ReactElement => {
  const { t } = useTranslation()
  const { watch } = useFormContext<ComplianceSettingsFormSchema>()
  const titleId = useComponentId('legal-mention-row-title')

  const customMention = watch(`legalMentions.custom.${legalMentionsType}`)

  const { defaultDescription, title } = useMemo(() => {
    switch (legalMentionsType) {
      case LegalMentionsType.EarlyPaymentDiscount:
        return {
          defaultDescription: t(
            'settings.organization.external_invoice.legal_mentions.early_payment_discount.description',
          ),
          title: t('settings.organization.external_invoice.legal_mentions.early_payment_discount.title'),
        }
      case LegalMentionsType.LatePaymentPenaltyRate:
        return {
          defaultDescription: t(
            'settings.organization.external_invoice.legal_mentions.late_payment_penalty_rate.default_description',
          ),
          title: t('settings.organization.external_invoice.legal_mentions.late_payment_penalty_rate.title'),
        }
    }
  }, [legalMentionsType, t])

  const description = customMention ?? defaultDescription

  const handleEditButtonClick = useCallback(() => {
    onEdit(legalMentionsType)
  }, [legalMentionsType, onEdit])

  return (
    <Flex alignItems="center" justifyContent="space-between">
      <Styled.LegalMentionRowContentWrapper>
        <Text id={titleId} weight="bold">
          {title}
        </Text>
        {description && (
          <Text colorVariant="secondary" truncate>
            {description}
          </Text>
        )}
      </Styled.LegalMentionRowContentWrapper>

      <Button aria-describedby={titleId} icon="squarePen" onClick={handleEditButtonClick} size="sm" variant="text">
        {t('settings.organization.external_invoice.legal_mentions.edit')}
      </Button>
    </Flex>
  )
}
